// Core types based on backend models and API contracts

import type { ChartDataSource } from "./chartDataSource";
import type { ComponentInteraction } from "./interactions";
import type { BillingInterval, SubscriptionTier } from "./billing";

// User types
export interface User {
  /** MongoDB user id returned by backend services. */
  _id?: string;
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  status: UserStatus;
  avatar?: string;
  bio?: string;
  phone?: string;
  emailVerifiedAt?: Date;
  lastLoginAt?: Date;
  preferences: UserPreferences;
  createdAt: Date;
  updatedAt: Date;
  /** Primary organization id returned by backend auth/user endpoints. */
  organizationId?: string;
  // Platform admin flag - members of platform admin org can view all data
  isPlatformAdmin?: boolean;
}

export enum UserRole {
  ADMIN = "admin",
  USER = "user",
  VIEWER = "viewer",
  MODERATOR = "moderator",
}

export enum UserStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  SUSPENDED = "suspended",
  PENDING = "pending",
}

export interface UserPreferences {
  theme: "light" | "dark" | "system";
  language: string;
  notifications: {
    email: boolean;
    desktop: boolean;
    collaboration: boolean;
  };
  editor: {
    snapToGrid: boolean;
    showGrid: boolean;
    gridSize: number;
    autoSave: boolean;
    autoSaveInterval: number;
  };
  homeProjectId?: string | null; // User's home project that opens on login
  homePageId?: string | null; // User's home page within the home project
  connections?: {
    searchableByEmail: boolean;
    searchableByUsername: boolean;
    acceptRequests: "anyone" | "nobody";
  };
  messaging?: {
    allowFromNonConnections: boolean;
    showReadReceipts: boolean;
    showOnlineStatus: boolean;
  };
}

// Authentication types
export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
  expiresAt: number; // Absolute timestamp in milliseconds when token expires
  tokenType: string;
}

export interface LoginCredentials {
  identifier: string; // email or username
  password: string;
  rememberMe?: boolean; // Extends token expiry when true
}

export type RegistrationSelectedPlan =
  | {
      tier: "free";
    }
  | {
      tier: Exclude<SubscriptionTier, "free" | "enterprise">;
      billingInterval: BillingInterval;
    };

export interface RegisterData {
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  password: string;
  legalAcceptance?: {
    accepted: boolean;
  };
  selectedPlan?: RegistrationSelectedPlan;
  publicAuthChallengeToken?: string;
  sponsorshipInviteToken?: string;
}

export interface SponsorshipRegistrationAcceptance {
  billingOrgName: string;
  redirectUrl: string;
  homepageProvisioningApplied: boolean;
  acceptedAt: string;
}

// Auth API response types
export interface AuthenticatedAuthResponse {
  user: User;
  tokens: AuthTokens;
  selectedPlan?: RegistrationSelectedPlan;
  verificationRequired?: false;
  pendingVerification?: false;
  status?: "authenticated" | "active";
  sponsorship?: SponsorshipRegistrationAcceptance;
}

export interface PendingRegistrationResponse {
  user?: Partial<User>;
  email?: string;
  verificationRequired?: true;
  pendingVerification?: true;
  status?: "pending" | "pending_verification" | "verification_required";
  nextStep?: "verify_email";
  selectedPlan?: RegistrationSelectedPlan;
  tokens?: undefined;
}

export type AuthResponse =
  | AuthenticatedAuthResponse
  | PendingRegistrationResponse;

export interface AuthenticatedRegisterResult {
  status: "authenticated";
  user: User;
  selectedPlan?: RegistrationSelectedPlan;
  sponsorship?: SponsorshipRegistrationAcceptance;
}

export interface PendingRegisterResult {
  status: "pending_verification";
  email: string;
  user?: User;
  message?: string;
  selectedPlan?: RegistrationSelectedPlan;
}

export type RegisterResult =
  | AuthenticatedRegisterResult
  | PendingRegisterResult;

export interface VerifyEmailResponse {
  verified?: true;
  status?: "verified";
  email?: string;
  nextAction?: "login" | "checkout" | "free";
  selectedPlan?: RegistrationSelectedPlan;
}

export interface ResendVerificationRequest {
  email: string;
  publicAuthChallengeToken?: string;
}

export interface VerifyEmailRequest {
  token: string;
  publicAuthChallengeToken?: string;
}

export interface ForgotPasswordRequest {
  email: string;
  publicAuthChallengeToken?: string;
}

export interface ForgotPasswordResponse {
  sent: true;
  status: "accepted";
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
  publicAuthChallengeToken?: string;
}

export interface ResetPasswordSuccessResponse {
  reset: true;
  status: "reset";
}

export interface ResetPasswordFailureResponse {
  reset: false;
  status: "invalid";
}

export interface ResendVerificationResponse {
  sent: true;
  status: "accepted";
}

export interface LegacyResendVerificationResponse {
  accepted: true;
  status?: "accepted";
}

export interface RefreshTokenRequest {
  refreshToken?: never;
}

export interface RefreshTokenResponse {
  tokens: AuthTokens;
}

// Project types
export interface Project {
  id: string;
  name: string;
  description: string;
  thumbnail?: string;
  // Organization/User ownership
  owner_type?: "User" | "Organization";
  owner_id?: string;
  // Legacy field (creator user ID)
  ownerId: string;
  members: ProjectMember[];
  visibility: ProjectVisibility;
  status: ProjectStatus;
  settings: ProjectSettings;
  tags: string[];
  currentVersion: number;
  schemaVersion?: 1 | 2;
  versions: ProjectVersion[];
  collaborationSession?: string;

  // Multi-page functionality
  pages: Page[];
  currentPageId?: string;
  navigation: ProjectNavigationSettings;
  isMultiPage: boolean;

  lastModifiedBy: string;
  lastModifiedAt: Date;
  isDeleted: boolean;
  deletedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProjectMember {
  userId: string;
  role: ProjectRole;
  permissions: ProjectPermission[];
  joinedAt: Date;
  invitedBy?: string;
  status: MemberStatus;
}

export enum ProjectRole {
  OWNER = "owner",
  ADMIN = "admin",
  EDITOR = "editor",
  VIEWER = "viewer",
}

export enum ProjectPermission {
  READ = "read",
  WRITE = "write",
  DELETE = "delete",
  SHARE = "share",
  ADMIN = "admin",
}

export enum MemberStatus {
  ACTIVE = "active",
  INVITED = "invited",
  REMOVED = "removed",
}

export enum ProjectVisibility {
  PRIVATE = "private",
  PUBLIC = "public",
  ORGANIZATION = "organization",
}

export enum ProjectStatus {
  ACTIVE = "active",
  ARCHIVED = "archived",
  DELETED = "deleted",
}

export interface ProjectSettings {
  autoSave: boolean;
  autoSaveInterval: number;
  maxVersions: number;
  allowComments: boolean;
  allowRealTimeEditing: boolean;
  requireApproval: boolean;

  // Multi-page settings
  allowPageCreation: boolean;
  maxPages: number;
  defaultPageTemplate: PageTemplate;
  enableCrossPageLinks: boolean;
  sharedComponentLibrary: boolean;
}

export interface ProjectNavigationSettings {
  navigationStyle: NavigationStyle;
  showPageTabs: boolean;
  showBreadcrumbs: boolean;
  enableSearch: boolean;
  compactMode: boolean;
  sidebarPosition: "left" | "right";
  tabsPosition: "top" | "bottom";
  customNavigationOrder: string[];
  navigationGroups: NavigationGroup[];
}

export enum NavigationStyle {
  TABS = "tabs",
  SIDEBAR = "sidebar",
  BREADCRUMB = "breadcrumb",
  HYBRID = "hybrid",
}

export interface NavigationGroup {
  id: string;
  name: string;
  icon?: string;
  order: number;
  collapsed: boolean;
  pageIds: string[];
}

export interface ProjectVersion {
  id: string;
  version: number;
  name?: string;
  description?: string;
  createdBy: string;
  createdAt: Date;
  snapshot: ProjectSnapshot;
  changes: VersionChange[];
  tags: string[];
  isPublished: boolean;
}

export interface ProjectSnapshot {
  // Legacy single-page support
  components?: Component[];
  canvasSettings?: CanvasSettings;

  // Multi-page support
  pages: PageSnapshot[];
  navigation: ProjectNavigationSettings;
  globalSettings: ProjectSettings;
  metadata: ProjectMetadata;
}

export interface PageSnapshot {
  pageId: string;
  pageName: string;
  components: Component[];
  canvasSettings: CanvasSettings;
  navigation: PageNavigationSettings;
  metadata: PageMetadata;
}

export interface ProjectMetadata {
  totalComponents: number;
  totalPages: number;
  lastEditedPageId?: string;
  complexity: "simple" | "moderate" | "complex";
  estimatedLoadTime: number;
  optimizationHints: string[];
}

export interface VersionChange {
  id?: string; // Unique identifier for the change
  type: "create" | "update" | "delete" | "move";
  target: "component" | "page" | "navigation" | "settings";
  targetId: string; // componentId or pageId
  pageId?: string; // For component changes
  field?: string;
  oldValue?: unknown;
  newValue?: unknown;
  userId: string;
  timestamp: Date;
  description?: string;
}

// Page types - Multi-page functionality
export interface Page {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  order: number;
  isDefault: boolean;
  isStarred?: boolean;
  template?: PageTemplate;
  navigation: PageNavigationSettings;
  permissions: PagePermissions;
  canvasSettings: CanvasSettings;
  metadata: PageMetadata;
  createdBy: string;
  lastModifiedBy: string;
  lastModifiedAt: Date;
  createdAt: Date;
  updatedAt: Date;
}

export enum PageTemplate {
  BLANK = "blank",
  DASHBOARD = "dashboard",
  REPORT = "report",
  FORM = "form",
  ANALYTICS = "analytics",
  CUSTOM = "custom",
}

export interface PageNavigationSettings {
  showInNavigation: boolean;
  navigationLabel?: string;
  icon?: string;
  navigationGroup?: string;
  order: number;
  parentPageId?: string;
  allowNavigation: boolean;
  requiresAuth: boolean;
}

export interface PagePermissions {
  canView: ProjectRole[];
  canEdit: ProjectRole[];
  canDelete: ProjectRole[];
  canShare: ProjectRole[];
  inheritFromProject: boolean;
  customPermissions: CustomPagePermission[];
}

export interface CustomPagePermission {
  userId: string;
  permissions: PagePermissionType[];
  grantedBy: string;
  grantedAt: Date;
}

export enum PagePermissionType {
  VIEW = "view",
  EDIT = "edit",
  DELETE = "delete",
  SHARE = "share",
  COMMENT = "comment",
}

// Version conflict type for conflict resolution
export interface VersionConflict {
  id: string;
  type:
    | "component_modified"
    | "component_deleted"
    | "page_modified"
    | "page_deleted"
    | "settings_modified";
  target: "component" | "page" | "navigation" | "settings";
  targetId: string;
  pageId?: string;
  field?: string;
  baseValue?: unknown;
  sourceValue?: unknown;
  targetValue?: unknown;
  resolution?: "use_source" | "use_target" | "merge" | "custom";
  customValue?: unknown;
  resolvedBy?: string;
  resolvedAt?: Date;
  description: string;
}

// Version branch for branch management
export interface VersionBranch {
  id: string;
  name: string;
  description?: string;
  baseVersion: number;
  headVersion: number;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  isProtected: boolean;
  mergedAt?: Date;
  mergedBy?: string;
  status: "active" | "merged" | "closed";
}

// Version draft for draft management
export interface VersionDraft {
  id: string;
  name: string;
  description?: string;
  baseVersion: number;
  branchId?: string;
  changes: VersionChange[];
  snapshot: ProjectSnapshot;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  isApproved?: boolean;
  approvedBy?: string;
  approvedAt?: Date;
  status: "draft" | "pending_review" | "approved" | "rejected";
}

// Version comparison result
export interface VersionComparison {
  id: string;
  fromVersion: number;
  toVersion: number;
  fromBranch?: string;
  toBranch?: string;
  changes: VersionChange[];
  conflictsDetected: boolean;
  conflicts: VersionConflict[];
  generatedAt: Date;
  generatedBy: string;
}

// Versioning metadata
export interface VersioningMetadata {
  totalVersions: number;
  totalBranches: number;
  totalDrafts: number;
  currentBranch?: string;
  lastVersionCreated?: Date;
  lastVersionCreatedBy?: string;
  hasUnresolvedConflicts: boolean;
  pendingDrafts: number;
}

export interface PageMetadata {
  lastViewedBy?: string;
  lastViewedAt?: Date;
  viewCount: number;
  editCount: number;
  componentCount: number;
  tags: string[];
  notes?: string;
  customFields: Record<string, unknown>;
}

/**
 * Component - Core component interface for TerraMatrix visual editor.
 *
 * Note: As of December 2025, TerraMatrix uses flex-only layout (AI-chat-only).
 * The `position` field is deprecated for x/y placement but retained for backward
 * compatibility with existing data. Use `zIndex` for stacking order.
 */
export interface Component {
  id: string;
  projectId: string;
  pageId: string; // Associate component with specific page
  name: string;
  type: ComponentType;
  /**
   * Component-specific properties. Common properties include:
   * - content: string (for Text components)
   * - src: string (for Image components)
   * - label: string (for Button components)
   * - tooltip?: TooltipConfig (custom hover tooltip - NOT for Chart/Map)
   *
   * @see TooltipConfig for tooltip configuration options
   */
  properties: Record<string, unknown>;
  /**
   * @deprecated Position is deprecated for x/y placement (flex-only architecture).
   * Use `zIndex` for stacking order. This field is optional for new components.
   */
  position?: Position;
  size: Size;
  style: Record<string, unknown>;
  responsivePosition: ResponsivePosition; // REQUIRED: All components must have responsive positioning
  parentId?: string;
  children: string[];
  /**
   * Pre-resolved child Component objects, injected by canvas rendering
   * pipelines that have already resolved child IDs. When present, takes
   * priority over resolving children[] by ID lookup against allComponents.
   *
   * This field is intentionally absent from the backend schema — it exists
   * only in the frontend rendering layer.
   */
  _childComponents?: Component[];
  order: number;
  /**
   * Stacking order for overlapping elements within flex containers.
   * Preferred over position.z for new code.
   */
  zIndex?: number;
  locked: boolean;
  hidden: boolean;
  version: number;
  createdBy: string;
  lastModifiedBy: string;
  lastModifiedAt: Date;
  isDeleted: boolean;
  deletedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  // Sub-component support for complex components (e.g., FermentationTankCard)
  isSubComponent?: boolean; // True for virtual sub-components that exist only in editor
  subComponentType?: string; // Semantic type: 'button', 'label', 'gauge', 'alert', etc.
  // Declarative component interactions (Phase 6)
  interactions?: ComponentInteraction[];
  metadata?: Record<string, unknown>;
}

/**
 * Input type for addComponent / addMultipleComponents.
 * Extends Component by making id, createdAt, and updatedAt optional so callers
 * can omit them (generated at construction time) or supply them (pattern-based
 * creation where the ID is already assigned).
 */
export type ComponentInput = Omit<
  Component,
  | "id"
  | "createdAt"
  | "updatedAt"
  | "projectId"
  | "style"
  | "size"
  | "children"
  | "order"
  | "locked"
  | "hidden"
  | "version"
  | "createdBy"
  | "lastModifiedBy"
  | "lastModifiedAt"
  | "isDeleted"
  | "responsivePosition"
> & {
  id?: string;
  projectId?: string;
  style?: Record<string, unknown>;
  size?: Size;
  children?: string[];
  order?: number;
  locked?: boolean;
  hidden?: boolean;
  version?: number;
  createdBy?: string;
  lastModifiedBy?: string;
  lastModifiedAt?: Date;
  isDeleted?: boolean;
  responsivePosition?: ResponsivePosition;
  createdAt?: Date;
  updatedAt?: Date;
};

/**
 * Update payload for updateComponent. Extends Partial<Component> with an
 * optional breakpoint field used by the AI quick-command pipeline to route
 * size updates into responsivePosition rather than the base size.
 * The breakpoint field is stripped from the update before it reaches the store.
 */
export type ComponentUpdateWithBreakpoint = Partial<Component> & {
  breakpoint?: string;
};

/**
 * Configuration for custom component tooltips.
 * Used for user-defined hover text on any non-chart/map component.
 *
 * Note: Chart and Map components have their own native tooltip system
 * controlled by `showTooltip: boolean` - this interface is NOT for those.
 *
 * @example
 * component.properties.tooltip = {
 *   text: "Click to learn more",
 *   enabled: true,
 *   side: "top",
 *   delayDuration: 400
 * }
 */
export interface TooltipConfig {
  /** The tooltip text content to display on hover */
  text: string;
  /** Whether the tooltip is active (default: true if text exists) */
  enabled?: boolean;
  /** Preferred placement relative to the trigger element */
  side?: "top" | "right" | "bottom" | "left";
  /** Milliseconds to wait before showing tooltip (default: 400) */
  delayDuration?: number;
}

export enum ComponentType {
  TEXT = "text",
  DATA_TEXT = "data-text", // Database-connected text primitive
  CONTAINER = "container",
  FLEX_CONTAINER = "flex-container",
  CARD = "card",
  INPUT = "input",
  BUTTON = "button",
  IMAGE = "image",
  ICON = "icon",
  CHART = "chart",
  BAR_CHART = "bar-chart",
  LINE_CHART = "line-chart",
  PIE_CHART = "pie-chart",
  SCATTER_CHART = "scatter-chart",
  AREA_CHART = "area-chart",
  HEATMAP_CHART = "heatmap-chart",
  TREEMAP_CHART = "treemap-chart",
  GAUGE_CHART = "gauge-chart",
  VERTICAL_GAUGE_CHART = "vertical-gauge-chart",
  DATA_CARD = "data-card",
  FERMENTATION_TANK_CARD = "fermentation-tank-card",
  TABLE = "table",
  ADVANCED_TABLE = "advanced-table",
  LIST = "list",
  CUSTOM = "custom",
  MAP_CHART = "map-chart",
  FORM_CONTAINER = "form-container",
  BREADCRUMB = "breadcrumb",
  FILE_STORAGE = "file-storage",
  KANBAN_BOARD = "kanban-board",
  RADIAL_DIAL = "radial-dial",
  TOGGLE_SWITCH = "toggle-switch",
  PUSH_BUTTON = "push-button",
  ICON_BUTTON = "icon-button",
  STATUS_LIGHT = "status-light",
  SLIDER = "slider",
  STEPPER = "stepper",
  LEVEL_INDICATOR = "level-indicator",
  SEGMENTED_CONTROL = "segmented-control",
  COLOR_PICKER = "color-picker",
  THERMOSTAT = "thermostat",
  MODAL = "modal",
  CHECKLIST = "checklist",
  HORIZONTAL_TIMELINE = "horizontal-timeline",
  ASSIGNMENT_WIDGET = "assignment-widget",
  GANTT_CHART = "gantt-chart",
  CALENDAR = "calendar",
  PROJECT_HUB = "project-hub",
  QUESTIONNAIRE = "questionnaire",
  VOTING_CARD = "voting-card",
  TIME_TRACKER = "time-tracker",
}

/**
 * @deprecated Position.x and Position.y are deprecated for component positioning.
 * TerraMatrix now uses flex-only layout - all component placement is via AI chat commands.
 * The z property is still used for stacking order within flex containers.
 *
 * For cursor tracking (collaboration), x and y are still valid.
 *
 * Migration: Components should use `zIndex` instead of `position.z` for stacking order.
 * The position field will be made optional in a future release.
 */
export interface Position {
  /** @deprecated Use flex layout instead of absolute x positioning */
  x: number;
  /** @deprecated Use flex layout instead of absolute y positioning */
  y: number;
  /** Stacking order within flex containers. Consider using Component.zIndex instead. */
  z: number;
}

/**
 * Positioning strategy for responsive components.
 * @note As of December 2025, TerraMatrix uses flex-only layout.
 * 'absolute' is deprecated but retained for backward compatibility.
 * Preferred strategies: 'flex', 'relative', 'auto'
 */
export type PositioningStrategy =
  | "absolute"
  | "relative"
  | "grid"
  | "flex"
  | "auto";

export interface ResponsiveConstraintValues {
  // Pin component to container edges
  pinToEdges?: {
    top?: boolean;
    right?: boolean;
    bottom?: boolean;
    left?: boolean;
  };
  // Position as percentage of container
  percentagePosition?: {
    x: number; // 0-100
    y: number; // 0-100
  };
  // CSS Grid placement
  gridArea?: string;
  gridColumn?: string;
  gridRow?: string;
  // Flexbox ordering
  flexOrder?: number;
  flexGrow?: number;
  flexShrink?: number;
  flexBasis?: string;
  // Alignment within container
  alignSelf?:
    | "auto"
    | "flex-start"
    | "flex-end"
    | "center"
    | "baseline"
    | "stretch";
  justifySelf?: "auto" | "start" | "end" | "center" | "stretch";
}

export interface ResponsiveConstraints extends ResponsiveConstraintValues {
  // v2 persisted templates store constraints in base/breakpoint slots.
  base?: ResponsiveConstraintValues;
  mobile?: ResponsiveConstraintValues;
  tablet?: ResponsiveConstraintValues;
  desktop?: ResponsiveConstraintValues;
}

export interface ResponsiveSize {
  width?:
    | number
    | string
    | "auto"
    | "fit-content"
    | "min-content"
    | "max-content";
  height?:
    | number
    | string
    | "auto"
    | "fit-content"
    | "min-content"
    | "max-content";
  minWidth?: number | string;
  maxWidth?: number | string;
  minHeight?: number | string;
  maxHeight?: number | string;
  aspectRatio?: number; // width/height ratio
}

export interface BreakpointOverride {
  position?: Partial<Position>;
  size?: Partial<ResponsiveSize>;
  constraints?: Partial<ResponsiveConstraints>;
  hidden?: boolean;
  style?: Record<string, unknown>;
  properties?: Record<string, unknown>;
}

export interface ResponsivePosition {
  // Positioning strategy for this component
  strategy: PositioningStrategy;
  // Responsive constraints
  constraints?: ResponsiveConstraints;
  // Size configuration
  responsiveSize?: ResponsiveSize;
  // Breakpoint-specific overrides
  breakpoints?: {
    mobile?: BreakpointOverride;
    tablet?: BreakpointOverride;
    desktop?: BreakpointOverride;
  };
  // Container layout mode (for container components)
  layoutMode?: "none" | "flex" | "grid";
  // Flex container properties
  flexDirection?: "row" | "column" | "row-reverse" | "column-reverse";
  flexWrap?: "nowrap" | "wrap" | "wrap-reverse";
  justifyContent?:
    | "flex-start"
    | "flex-end"
    | "center"
    | "space-between"
    | "space-around"
    | "space-evenly";
  alignItems?: "flex-start" | "flex-end" | "center" | "baseline" | "stretch";
  gap?: number | string;
  // Grid container properties
  gridTemplateColumns?: string;
  gridTemplateRows?: string;
  gridTemplateAreas?: string;
  gridGap?: number | string;
  gridAutoFlow?: "row" | "column" | "row dense" | "column dense";
}

export interface Size {
  width: number | string;
  height: number | string;
}

// Canvas types
export interface CanvasSettings {
  background: CanvasBackground;
  grid: GridSettings;
  layout: CanvasLayout;
  theme: CanvasTheme;
  responsive: ResponsiveSettings;
}

export interface CanvasBackground {
  type: "color" | "gradient" | "image" | "pattern";
  color: string;
  gradient?: {
    type: "linear" | "radial";
    direction: string;
    stops: { color: string; position: number }[];
  };
  image?: {
    url: string;
    size: "cover" | "contain" | "auto";
    position: string;
    repeat: "no-repeat" | "repeat" | "repeat-x" | "repeat-y";
    opacity: number;
  };
  pattern?: {
    type: "dots" | "lines" | "grid" | "diagonal";
    color: string;
    size: number;
    spacing: number;
    opacity: number;
  };
}

export interface GridSettings {
  enabled: boolean;
  size: number;
  color: string;
  opacity: number;
  style: "solid" | "dashed" | "dotted";
  majorLines: {
    enabled: boolean;
    interval: number;
    color: string;
    opacity: number;
  };
  snapToGrid: boolean;
  snapThreshold: number;
}

export interface CanvasLayout {
  padding: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  maxWidth: number | null;
  maxHeight: number | null;
  centerContent: boolean;
  overflow: "visible" | "hidden" | "scroll" | "auto";
}

export interface CanvasTheme {
  name: string;
  colorScheme: "light" | "dark" | "auto";
  accentColor: string;
  borderRadius: number;
  shadows: boolean;
  animations: boolean;
}

export interface ResponsiveSettings {
  breakpoints: {
    desktop: number;
    tablet: number;
    mobile: number;
  };
  scaling: {
    desktop: number;
    tablet: number;
    mobile: number;
  };
  hideElementsOnMobile: boolean;
  touchOptimization: boolean;
}

// Icon types
export enum IconLibrary {
  LUCIDE = "lucide",
  RADIX = "radix",
  CUSTOM = "custom",
}

export interface IconProperties {
  iconName: string; // e.g., "Star", "Users", "PersonIcon"
  library: IconLibrary; // "lucide" | "radix"
  size: number; // 16-128px
  color?: string; // Hex color
  strokeWidth?: number; // 1-4 (lucide only)
  rotation?: number; // 0-360 degrees
  fill?: boolean; // filled vs outlined (lucide only)
  opacity?: number; // 0-1
  label?: string; // Accessibility label
  // Navigation properties
  action?: "navigate" | undefined; // Action type
  target?: string; // Page ID for navigation
  navigationLabel?: string; // Display name for the target page
}

// Button types
export interface ButtonProperties {
  text?: string; // Button label text
  variant?: string; // "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
  size?: string; // "default" | "sm" | "lg" | "icon"
  disabled?: boolean; // Disabled state
  loading?: boolean; // Loading state
  type?: string; // "button" | "submit" | "reset"

  // Icon properties
  iconName?: string; // Icon identifier (e.g., "Check", "ArrowRight")
  iconLibrary?: IconLibrary; // Icon library ("lucide" | "radix")
  iconPosition?: "left" | "right"; // Icon placement relative to text
  iconSize?: number; // Icon size in pixels (defaults to inherit from button)
  iconColor?: string; // Icon color (defaults to button text color)

  // Action properties
  action?: string; // Action type (e.g., "navigate", "api-call", "sensor-control")
  target?: string; // Action target (e.g., pageId for navigation)
  navigationLabel?: string; // Display name for navigation target
  data?: Record<string, unknown>; // Additional data for actions

  // Styling properties
  fullWidth?: boolean; // Full width button
  hoverBackgroundColor?: string; // Background color on hover
  hoverColor?: string; // Text color on hover
}

// Card Properties (generic container card component)
export interface CardProperties {
  // Visual properties (card-specific defaults)
  backgroundColor?: string; // Default: "#ffffff"
  borderWidth?: number; // Default: 1
  borderColor?: string; // Default: "#e5e5e5"
  borderStyle?: "solid" | "dashed" | "dotted" | "none"; // Default: "solid"
  borderRadius?: number; // Default: 12
  boxShadow?: string; // Default: "0 2px 8px rgba(0,0,0,0.08)"
  padding?: number | string; // Default: 24

  // Layout properties (simplified from FlexContainer)
  flexDirection?: "row" | "column"; // Default: "column"
  gap?: number; // Default: 16
  alignItems?: "stretch" | "flex-start" | "center" | "flex-end"; // Default: "stretch"
  justifyContent?:
    | "flex-start"
    | "center"
    | "flex-end"
    | "space-between"
    | "space-around"; // Default: "flex-start"

  // Sizing constraints
  minWidth?: number | string;
  maxWidth?: number | string;
  minHeight?: number | string;
  maxHeight?: number | string;

  // Optional hover effects
  hoverBackgroundColor?: string;
  hoverBorderColor?: string;
  hoverShadow?: string;

  // Metadata
  label?: string;
}

// DataCard Properties
export interface DataCardProperties {
  label?: string;
  value?: number | string;
  valueFormatter?: string;
  /** Optional IANA timezone override for date/time formatting */
  timeZone?: string;
  /** Decimal places for number formatting */
  decimalPlaces?: number;
  /** Use thousands separator */
  thousandsSeparator?: boolean;
  /** Currency symbol for currency formatter */
  currencySymbol?: string;
  /** Intl currency code for currency formatter */
  currencyCode?: string;
  unit?: string;
  subtitle?: string;
  trend?: {
    value?: number;
    label?: string;
    direction?: "up" | "down" | "neutral";
  };
  icon?: string;
  iconPosition?: "left" | "right" | "top" | "none";
  iconSize?: number;
  iconColor?: string;
  iconBackgroundColor?: string;
  iconBackgroundSize?: number;
  showIconBackground?: boolean;
  layout?: "horizontal" | "vertical";
  gap?: number;
  alignItems?: string;
  justifyContent?: string;
  valueAlignment?: "left" | "center" | "right";
  cardBackgroundColor?: string;
  cardBorderColor?: string;
  cardBorderWidth?: number;
  cardBorderRadius?: number;
  cardShadow?: string;
  cardPadding?: string | number;
  labelColor?: string;
  labelFontSize?: string;
  labelFontWeight?: number;
  labelLetterSpacing?: string;
  valueColor?: string;
  valueFontSize?: string;
  valueFontWeight?: number;
  subtitleColor?: string;
  subtitleFontSize?: string;
  subtitleFontWeight?: number;
  trendFontSize?: string;
  trendColorPositive?: string;
  trendColorNegative?: string;
  showTrendIcon?: boolean;
  // DATA TEXT INTEGRATION (Phase 8)
  /** Enable data binding mode using DataText capabilities */
  useDataText?: boolean;
  /** Data source configuration for fetching value */
  valueDataSource?: ChartDataSource;
  /** Field name to extract value from data source (default: 'value') */
  valueField?: string;
  /** Enable dynamic label from data source */
  useDynamicLabel?: boolean;
  /** Field name to extract label from data source */
  labelField?: string;
  /** Text to display when value is null/undefined (default: '--') */
  emptyValueText?: string;
  /** Show loading indicator while fetching data */
  showLoadingState?: boolean;
  /** Show error state when data fetch fails */
  showErrorState?: boolean;
  // Corner action icons
  /** Show info icon in top-right corner */
  showInfoIcon?: boolean;
  /** Size of info icon in pixels (default: 14, compact: 12) */
  infoIconSize?: number;
  /** Color of info icon (default: #9CA3AF) */
  infoIconColor?: string;
  /** Show navigation arrow icon in bottom-right corner */
  showNavigationIcon?: boolean;
  /** Size of navigation icon in pixels (default: 16, compact: 14) */
  navigationIconSize?: number;
  /** Color of navigation icon (default: #6B7280) */
  navigationIconColor?: string;
}

/**
 * DataTextProperties - Properties for database-connected text primitive.
 *
 * DataText displays a single dynamic value from a database query with full
 * text styling capabilities. It is a TEXT PRIMITIVE (peer to TEXT), not a
 * chart or card component.
 */
export interface DataTextProperties {
  // ═══════════════════════════════════════════════════════════════════════════
  // DATA BINDING
  // ═══════════════════════════════════════════════════════════════════════════
  /** Data source configuration for dynamic values */
  dataSource?: ChartDataSource;
  /** Query template ID for direct template execution */
  queryTemplateId?: string;
  /** Field name to extract from query result row */
  valueField?: string;
  /** Optional field for dynamic label from same query */
  labelField?: string;

  // ═══════════════════════════════════════════════════════════════════════════
  // STATIC FALLBACK
  // ═══════════════════════════════════════════════════════════════════════════
  /** Static label text */
  label?: string;
  /** Static value when dataSource not configured */
  value?: string | number;
  /** Unit to display after value (e.g., "°C", "kg", "%") */
  unit?: string;
  /** Prefix before value (e.g., "$", "#") */
  prefix?: string;
  /** Suffix after value */
  suffix?: string;

  // ═══════════════════════════════════════════════════════════════════════════
  // VALUE FORMATTING
  // ═══════════════════════════════════════════════════════════════════════════
  /** Value formatter type */
  valueFormatter?:
    | "number"
    | "currency"
    | "percent"
    | "date"
    | "time"
    | "datetime"
    | "compact"
    | "integer"
    | "decimal"
    | "custom";
  /** Optional IANA timezone override for date/time formatting */
  timeZone?: string;
  /** Backwards-compatible lowercase alias for timezone override */
  timezone?: string;
  /** Decimal places for number formatting */
  decimalPlaces?: number;
  /** Use thousands separator */
  thousandsSeparator?: boolean;
  /** Currency symbol for currency formatter */
  currencySymbol?: string;
  /** Intl currency code for currency formatter */
  currencyCode?: string;
  /** Custom format string */
  customFormat?: string;

  // ═══════════════════════════════════════════════════════════════════════════
  // VALUE TYPOGRAPHY
  // ═══════════════════════════════════════════════════════════════════════════
  /** Font size (e.g., "16px", "1.5rem") */
  fontSize?: string;
  /** Font weight (100-900) */
  fontWeight?: number;
  /** Font family */
  fontFamily?: string;
  /** Text color */
  color?: string;
  /** Text alignment */
  textAlign?: "left" | "center" | "right";
  /** Line height */
  lineHeight?: string | number;
  /** Letter spacing */
  letterSpacing?: string;

  // ═══════════════════════════════════════════════════════════════════════════
  // LABEL TYPOGRAPHY
  // ═══════════════════════════════════════════════════════════════════════════
  /** Whether to show label */
  showLabel?: boolean;
  /** Label font size */
  labelFontSize?: string;
  /** Label color */
  labelColor?: string;
  /** Label font weight */
  labelFontWeight?: number;
  /** Label position relative to value */
  labelPosition?: "above" | "below" | "left" | "right";

  // ═══════════════════════════════════════════════════════════════════════════
  // UNIT TYPOGRAPHY
  // ═══════════════════════════════════════════════════════════════════════════
  /** Unit font size (defaults to smaller than value) */
  unitFontSize?: string;
  /** Unit color */
  unitColor?: string;
  /** Gap between value and unit/suffix in pixels (default: 2) */
  unitGap?: number;

  // ═══════════════════════════════════════════════════════════════════════════
  // LAYOUT
  // ═══════════════════════════════════════════════════════════════════════════
  /** Layout direction for label + value */
  layout?: "inline" | "stacked";
  /** Gap between label and value */
  gap?: number;

  // ═══════════════════════════════════════════════════════════════════════════
  // STATE HANDLING
  // ═══════════════════════════════════════════════════════════════════════════
  /** Show loading indicator while fetching */
  showLoadingState?: boolean;
  /** Text to show while loading */
  loadingText?: string;
  /** Show error state on fetch failure */
  showErrorState?: boolean;
  /** Text to show on error */
  errorText?: string;
  /** Text to show when value is null/undefined */
  emptyValueText?: string;

  // ═══════════════════════════════════════════════════════════════════════════
  // CONDITIONAL FORMATTING (Phase 4.1)
  // ═══════════════════════════════════════════════════════════════════════════
  /** Enable value-based conditional coloring */
  useConditionalColor?: boolean;
  /** Color for positive values (default: '#059669' - green) */
  positiveColor?: string;
  /** Color for negative values (default: '#DC2626' - red) */
  negativeColor?: string;
  /** Color for zero values (default: '#6B7280' - gray) */
  zeroColor?: string;
  /** Threshold above which value is considered positive (default: 0) */
  positiveThreshold?: number;
  /** Threshold below which value is considered negative (default: 0) */
  negativeThreshold?: number;

  // ═══════════════════════════════════════════════════════════════════════════
  // VALUE ANIMATION (Phase 4.2)
  // ═══════════════════════════════════════════════════════════════════════════
  /** Enable smooth transitions on value changes */
  animateValueChange?: boolean;
  /** Animation duration in milliseconds (100-2000, default: 300) */
  animationDuration?: number;
  /** Type of animation for value changes */
  animationType?: "fade" | "slide" | "count" | "scale" | "none";
  /** Easing function for animations */
  animationEasing?: "ease" | "ease-in" | "ease-out" | "ease-in-out" | "linear";

  // ═══════════════════════════════════════════════════════════════════════════
  // CHART HOVER SYNCHRONIZATION (Compound Chart)
  // ═══════════════════════════════════════════════════════════════════════════
  /** ID of linked chart component for hover synchronization */
  linkedChartId?: string;
  /** Field to extract from hovered data point (defaults to valueField) */
  hoverValueField?: string;
  /** Show current value when not hovering (default: true) */
  showCurrentOnIdle?: boolean;

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPUTED EXPRESSIONS
  // ═══════════════════════════════════════════════════════════════════════════
  /** Math expression for computed KPI values.
   *  Supports: arithmetic (+, -, *, /), parentheses, min(), max(), abs(), round(v, d), floor(), ceil()
   *  References: {{componentId.queryData[0].fieldName}} or {{this.queryData[0].fieldName}}
   *  Example: "{{weight-kpi.queryData[0].last_weight}} / {{goal-kpi.queryData[0].last_goalWeight}} * 100" */
  computedExpression?: string;
  /** Component IDs whose query data this expression depends on.
   *  Used to delay evaluation until all dependencies have loaded.
   *  Auto-derived from computedExpression if not explicitly set. */
  expressionDependencies?: string[];

  // ═══════════════════════════════════════════════════════════════════════════
  // REFRESH BADGE SYNCHRONIZATION
  // ═══════════════════════════════════════════════════════════════════════════
  /**
   * Badge scope container ID used for timestamp-aggregate refresh
   * synchronization. Set by AI command handlers when placing this component
   * inside a badge-scoped container.
   */
  refreshBadgeScopeId?: string;
}

export interface RefreshBadgeConfig {
  enabled: boolean;
  timestampComponentId: string;
  scopeContainerId: string;
  timestampFormat?: "relative" | "absolute";
}

// FermentationTankCard Properties
export interface FermentationTankCardProperties {
  // Tank identification
  tankId?: string;
  label?: string;

  // Data fetching
  dataEndpoint?: string;
  refreshInterval?: number;
  cubeQuery?: Record<string, unknown>; // Cube.js query object

  // Static data values
  fillLevel?: number;
  currentTemp?: number;
  targetTemp?: number;
  temperatureUnit?: "F" | "C";
  glycolStatus?: "active" | "inactive";
  batchNumber?: string;
  beerType?: string;
  startDate?: string;
  projectedEnd?: string;
  alerts?: Array<{
    id: string;
    severity: "info" | "warning" | "error";
    message: string;
    timestamp?: string;
  }>;

  // Alert thresholds and conditions
  tempWarningDelta?: number; // Temperature warning threshold (±degrees from target)
  tempErrorDelta?: number; // Temperature error threshold (±degrees from target)
  fillLevelLow?: number; // Low fill level warning threshold (%)
  fillLevelHigh?: number; // High fill level warning threshold (%)
  alertOnGlycolInactive?: boolean; // Show alert when glycol cooling is inactive

  // Gauge styling
  gaugeColors?: string[];
  gaugeBorderColor?: string;
  gaugeBorderWidth?: number;
  gaugeBorderRadius?: number;
  gaugeBackgroundColor?: string;
  gaugeFillOpacity?: number;
  gaugeValueColor?: string;

  // Temperature styling
  currentTempColor?: string;
  targetTempColor?: string;
  tempLabelColor?: string;

  // Alert colors
  alertColorInfo?: string;
  alertColorWarning?: string;
  alertColorError?: string;

  // Card styling
  cardBackgroundColor?: string;
  cardBorderColor?: string;
  cardBorderRadius?: number;
  cardPadding?: string;
  cardGap?: number;

  // Layout control
  tankVisualWidth?: string;
  statusDisplayWidth?: string;
  sectionGap?: number;

  // Visibility toggles
  showGlycolStatus?: boolean;
  showBatchInfo?: boolean;
  showAlerts?: boolean;
  showActionButtons?: boolean;

  // Section ordering (0 = first)
  glycolStatusOrder?: number;
  temperatureDisplayOrder?: number;
  batchInfoOrder?: number;
  alertsOrder?: number;
  actionButtonsOrder?: number;

  // Action buttons
  actionButtons?: Array<{
    id: string;
    label: string;
    icon?: string;
    variant?: "ghost" | "outline" | "solid";
    visible?: boolean;
  }>;

  // View Details Button (following ButtonProperties pattern)
  showViewDetailsButton?: boolean;
  viewDetailsLabel?: string;
  viewDetailsAction?: string; // "navigate" for internal, undefined for external
  viewDetailsTarget?: string; // pageId or URL
  viewDetailsNavigationLabel?: string; // Human-readable name for navigation target
}

// Editor types
export interface EditorState {
  selectedComponents: string[];
  hoveredComponent?: string;
  previewHighlightId?: string; // Component to highlight during clarification hover
  draggedComponent?: string;
  viewport: Viewport;
  zoom: number;
  gridEnabled: boolean;
  gridSize: number;
  snapToGrid: boolean;
  showGuides: boolean;
  isPreviewMode: boolean;
  activePanel: EditorPanel;
  canvasSelected: boolean;

  // Multi-page editor state
  currentPageId?: string;
  pageHistory: string[];
  crossPageClipboard: Component[];
  navigationVisible: boolean;
  pageSearchQuery?: string;
}

export enum Viewport {
  DESKTOP = "desktop",
  TABLET = "tablet",
  MOBILE = "mobile",
}

export enum EditorPanel {
  COMPONENTS = "components",
  PROPERTIES = "properties",
  LAYERS = "layers",
  HISTORY = "history",
  ASSETS = "assets",
  CANVAS = "canvas",
  PAGES = "pages", // NEW: Page management panel
}

export interface ViewportConfig {
  name: string;
  width: number;
  height: number;
  icon: string;
  breakpoint: string;
}

// Collaboration types
export interface CollaborationSession {
  id: string;
  projectId: string;
  participants: Participant[];
  activeUsers: string[];
  createdAt: Date;
  lastActivity: Date;
}

export interface Participant {
  userId: string;
  joinedAt: Date;
  lastSeen: Date;
  cursor?: CursorPosition;
  selection?: string[];
}

export interface CursorPosition {
  x: number;
  y: number;
  viewport: Viewport;
}

export interface CollaborationEvent {
  type: CollaborationEventType;
  userId: string;
  timestamp: Date;
  data: unknown;
}

export enum CollaborationEventType {
  USER_JOINED = "user_joined",
  USER_LEFT = "user_left",
  CURSOR_MOVED = "cursor_moved",
  COMPONENT_SELECTED = "component_selected",
  COMPONENT_UPDATED = "component_updated",
  COMPONENT_CREATED = "component_created",
  COMPONENT_DELETED = "component_deleted",
  PROJECT_UPDATED = "project_updated",

  // Multi-page collaboration events
  PAGE_CREATED = "page_created",
  PAGE_UPDATED = "page_updated",
  PAGE_DELETED = "page_deleted",
  PAGE_SWITCHED = "page_switched",
  COMPONENT_MOVED_BETWEEN_PAGES = "component_moved_between_pages",
  NAVIGATION_UPDATED = "navigation_updated",
}

// AI types
export interface AIRequest {
  type: AIRequestType;
  prompt: string;
  context?: unknown;
  options?: AIRequestOptions;
}

export enum AIRequestType {
  GENERATE_COMPONENT = "generate_component",
  MODIFY_COMPONENT = "modify_component",
  SUGGEST_LAYOUT = "suggest_layout",
  OPTIMIZE_DESIGN = "optimize_design",
  GENERATE_CONTENT = "generate_content",
}

export interface AIRequestOptions {
  provider?: AIProvider;
  model?: string;
  temperature?: number;
  maxTokens?: number;
  streaming?: boolean;
}

export enum AIProvider {
  OPENAI = "openai",
  ANTHROPIC = "anthropic",
  GEMINI = "gemini",
  GROK = "grok",
}

export interface AIResponse {
  id: string;
  type: AIRequestType;
  result: unknown;
  confidence: number;
  provider: AIProvider;
  model: string;
  metadata: {
    tokensUsed: number;
    processingTime: number;
    cached: boolean;
    fallbackUsed?: boolean;
    retryCount?: number;
  };
  createdAt: Date;
}

// API types
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  pagination?: PaginationInfo;
}

// Multi-page API request/response types
export interface CreatePageRequest {
  name: string;
  description?: string;
  template: PageTemplate;
  order?: number;
  navigation?: Partial<PageNavigationSettings>;
  permissions?: Partial<PagePermissions>;
  copyFromPageId?: string;
}

export interface UpdatePageRequest {
  name?: string;
  description?: string;
  template?: PageTemplate;
  order?: number;
  isStarred?: boolean;
  navigation?: Partial<PageNavigationSettings>;
  permissions?: Partial<PagePermissions>;
  canvasSettings?: Partial<CanvasSettings>;
}

export interface PageSaveData {
  components: Component[];
  canvasSettings: CanvasSettings;
  metadata?: Partial<PageMetadata>;
}

export interface BulkPageOperation {
  operation: "reorder" | "delete" | "duplicate" | "archive";
  pageIds: string[];
  parameters?: Record<string, unknown>;
}

export interface CrossPageComponentMove {
  componentId: string;
  sourcePageId: string;
  targetPageId: string;
  position?: Position;
  maintainReferences?: boolean;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
  totalPages?: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
  stack?: string;
  status?: number;
}

// Theme and UI types
export interface Theme {
  name: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    border: string;
    success: string;
    warning: string;
    error: string;
  };
  spacing: Record<string, string>;
  typography: Record<string, unknown>;
  shadows: Record<string, string>;
  borderRadius: Record<string, string>;
}

export interface UIState {
  sidebarCollapsed: boolean;
  rightPanelCollapsed: boolean;
  activeModals: string[];
  notifications: Notification[];
  loading: boolean;
  theme: "light" | "dark" | "system";
}

export interface Notification {
  id: string;
  type: "success" | "warning" | "error" | "info";
  title: string;
  message: string;
  duration?: number;
  actions?: NotificationAction[];
  createdAt: Date;
}

export interface NotificationAction {
  label: string;
  action: () => void;
  variant?: "primary" | "secondary";
}

// Form types
export interface FormField {
  name: string;
  label: string;
  type:
    | "text"
    | "email"
    | "password"
    | "number"
    | "select"
    | "checkbox"
    | "textarea";
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  validation?: Record<string, unknown>;
}

// Operational Transform types
export interface Operation {
  id: string;
  type: OperationType;
  componentId?: string;
  componentData?: unknown;
  field?: string;
  value?: unknown;
  oldValue?: unknown;
  position?: number;
  length?: number;
  userId: string;
  timestamp: Date;
  vector: VectorClock;
  dependencies: string[];
  /** Sensor update payload data */
  data?: unknown;
}

export enum OperationType {
  INSERT = "insert",
  DELETE = "delete",
  RETAIN = "retain",
  UPDATE = "update",
  MOVE = "move",
  CREATE_COMPONENT = "create_component",
  DELETE_COMPONENT = "delete_component",
  SENSOR_UPDATE = "sensor_update",
}

export interface VectorClock {
  [userId: string]: number;
}

// Chart and Table specific types
export interface ChartDataPoint {
  [key: string]: unknown;
}

export interface ChartConfig {
  title?: string;
  description?: string;
  data: ChartDataPoint[];
  xAxisKey: string;
  yAxisKey: string;
  colorKey?: string;
  showLegend?: boolean;
  showTooltip?: boolean;
  showGrid?: boolean;
  colors?: string[];
  responsive?: boolean;
  animation?: boolean;
  margin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
}

export interface TableColumn {
  id: string;
  accessorKey: string;
  sourceKey?: string;
  header: string;
  cell?: string;
  sortable?: boolean;
  filterable?: boolean;
  size?: number;
  minSize?: number;
  maxSize?: number;
  enableResizing?: boolean;
  enableSorting?: boolean;
  enableColumnFilter?: boolean;
  enableGlobalFilter?: boolean;
  meta?: Record<string, unknown>;
}

export interface TableConfig {
  columns: TableColumn[];
  data: Record<string, unknown>[];
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enableGlobalFilter?: boolean;
  enableColumnResizing?: boolean;
  enableRowSelection?: boolean;
  enablePagination?: boolean;
  pageSize?: number;
  enableVirtualization?: boolean;
  maxHeight?: number;
  striped?: boolean;
  hoverable?: boolean;
  showBorders?: boolean;
  headerStyle?: "background" | "typography";
  headerBackgroundColor?: string;
  headerTextColor?: string;
  rowSpacing?: "none" | "tight" | "normal" | "loose";
  rowPadding?: "compact" | "normal" | "spacious";
  dashboardMode?: boolean;
  // Visual styling properties
  bodyTextColor?: string;
  alternateRowColor?: string;
  rowHoverColor?: string;
  borderColor?: string;
  // NOTE: TableColumn also has legacy `sortable`/`filterable` fields
  // alongside `enableSorting`/`enableColumnFilter`. Existing templates may use either form.
}

export interface MarketplaceComponent {
  id: string;
  name: string;
  description: string;
  version: string;
  author: string;
  category: string;
  tags: string[];
  downloads: number;
  rating: number;
  reviewCount: number;
  price: number;
  license: string;
  framework: string[];
  dependencies: string[];
  repository?: string;
  documentation?: string;
  demo?: string;
  screenshots: string[];
  component: Component;
  createdAt: Date;
  updatedAt: Date;
}

export interface MarketplaceSearchQuery {
  query?: string;
  category?: string;
  subcategory?: string;
  tags?: string[];
  license?: string | string[];
  minRating?: number;
  maxRating?: number;
  priceModel?: string[];
  minPrice?: number;
  maxPrice?: number;
  frameworks?: string[];
  featured?: boolean;
  trending?: boolean;
  verified?: boolean;
  sortBy?: "relevance" | "downloads" | "rating" | "price" | "updated";
  sortOrder?: "asc" | "desc";
}

export interface MarketplaceSearchResult {
  components: MarketplaceComponent[];
  totalCount: number;
  facets: {
    categories: { name: string; count: number }[];
    tags: { name: string; count: number }[];
    licenses: { name: string; count: number }[];
    priceModels: { name: string; count: number }[];
    frameworks: { name: string; count: number }[];
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// Utility types
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends Record<PropertyKey, unknown>
    ? DeepPartial<T[P]>
    : T[P];
};

export type Nullable<T> = T | null;

export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type RequireAtLeastOne<T, Keys extends keyof T = keyof T> = Pick<
  T,
  Exclude<keyof T, Keys>
> &
  {
    [K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>;
  }[Keys];

// Color Palette types
export * from "./colorPalette";

// Style Preset types
export * from "./stylePreset";

// User Connection types
export * from "./connections";

// Messaging types
export * from "./messaging";

// Chart Navigation types
export * from "./chartNavigation";

// Style Intelligence types
export * from "./styleIntelligence";

// Color scheme render/write context types
export * from "./color-scheme";

// Component dataset binding contracts
export type {
  CatalogDataSourceRef,
  CatalogSourceCapabilities,
  CatalogSourceError,
  CatalogSourceFieldConstraints,
  CatalogSourceFieldMetadata,
  CatalogSourceLabels,
  CatalogSourceOwnerType,
  CatalogSourceReconnectReason,
  CatalogSourceResolveOperation,
  CatalogSourceResolveRequest,
  CatalogSourceResolveResponse,
  CatalogSourceResolveSourceRef,
  ComponentDataBinding,
  ComponentDataBindingSource,
  ComponentDataTemplateCreateRequest,
  ComponentDataTemplateCreateResponse,
  ComponentDataTemplateExecutableCreateResponse,
  ComponentDataTemplateExecutableSource,
  ComponentDataTemplateNeedsReviewResponse,
  DataBindingFieldMapping,
  DataBindingOwnerType,
  DataCatalogSelection,
  DataSourceRef,
  DataViewKind,
  DataViewRef,
  FederatedViewDefinition,
  FederatedViewPermissionMode,
  FederatedViewSource,
  FederatedViewVisibility,
  ResolvedCatalogSource,
  StaticDataBindingRef,
} from "./componentDataBinding";
export {
  COMPONENT_DATA_TEMPLATE_MAX_SOURCES,
  isCatalogSourceCapabilities,
  isCatalogSourceFieldConstraints,
  isCatalogSourceFieldMetadata,
  isCatalogSourceLabels,
  isCatalogSourceResolutionError,
  isCatalogSourceResolveRequest,
  isCatalogSourceResolveResponse,
  isCatalogSourceResolveSourceRef,
  getRecordArray,
  isComponentDataBinding,
  isComponentDataBindingSource,
  isComponentDataTemplateCreateRequest,
  isComponentDataTemplateCreateResponse,
  isDataBindingFieldMapping,
  isDataSourceOrViewRef,
  isDataSourceRef,
  isDataViewRef,
  isFederatedViewDefinition,
  isFederatedViewSource,
  isLegacyChartDataSource,
  isRecord,
  isRecordArray,
  isResolvedCatalogSource,
  isStaticDataBindingRef,
  isUnknownRecord,
  parseCatalogSourceResolveRequest,
  parseCatalogSourceResolveResponse,
  parseComponentDataBinding,
  parseComponentDataTemplateCreateRequest,
  parseComponentDataTemplateCreateResponse,
  parseDataSourceRef,
  parseDataViewRef,
  parseFederatedViewDefinition,
} from "./componentDataBinding";

// --- Service Provider & Client Project Types ---

export type Specialization =
  | "electrical"
  | "plumbing"
  | "hvac"
  | "security"
  | "networking"
  | "solar"
  | "av"
  | "iot"
  | "lighting"
  | "landscaping"
  | "automation"
  | "general"
  | "marketing"
  | "sales"
  | "consulting"
  | "business_strategy"
  | "accounting"
  | "it_services"
  | "web_development"
  | "software"
  | "cybersecurity"
  | "data_analytics"
  | "design"
  | "graphic_design"
  | "content_creation"
  | "photography"
  | "video_production"
  | "other";
export type CertificationTier =
  | "registered"
  | "certified"
  | "qualified_partner"
  | "ambassador";
export type ClientProjectStatus =
  | "setup"
  | "configured"
  | "handoff_pending"
  | "active"
  | "archived";
export type EngagementStatus =
  | "pending"
  | "active"
  | "expired"
  | "completed"
  | "cancelled"
  | "revoked";
export type ClientProjectType =
  | "residential"
  | "commercial"
  | "industrial"
  | "mixed";

export interface ServiceProviderProfile {
  _id: string;
  userId: string;
  specializations: Specialization[];
  otherSpecialization?: string;
  certificationTier: CertificationTier;
  businessName?: string;
  businessDescription?: string;
  businessPhone?: string;
  businessEmail?: string;
  businessWebsite?: string;
  employeeCount?: number;
  businessAddress?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  serviceArea?: {
    type: "Point";
    coordinates: [number, number];
    radiusKm?: number;
  };
  ratings: { average: number; count: number };
  completedEngagements: number;
  activeEngagementCount: number;
  trainingRecords?: {
    name: string;
    completedAt: string;
    certificateUrl?: string;
  }[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ClientProject {
  _id: string;
  name: string;
  address: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  projectType: ClientProjectType;
  customerEmail: string;
  customerName?: string;
  customerOrg: string;
  dealerOfRecord: string;
  linkedProjects: Array<{
    projectId: string;
    isPrimary: boolean;
    specializationScope?: Specialization;
    addedBy: string;
    addedAt: string;
  }>;
  provisioningConfig?: {
    homeProjectId?: string;
    homePageId?: string;
    quickPages?: Array<{ pageId: string; label: string; icon?: string }>;
    theme?: string;
  };
  handoff: {
    invitationToken?: string;
    tokenExpiry?: string;
    status: "none" | "pending" | "sent" | "accepted" | "declined";
    emailStatus?: "sent" | "send_failed";
    acceptedAt?: string;
    acceptedBy?: string;
    reminders: Array<{ sentAt: string; type: string }>;
  };
  sourceTemplateId?: string;
  status: ClientProjectStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceEngagement {
  _id: string;
  clientProjectId: string;
  serviceProviderId: string;
  serviceProviderUserId: string;
  specializationScope: Specialization;
  status: EngagementStatus;
  accessWindow: { startDate: string; endDate: string };
  projectIds: string[];
  createdBy: string;
  workLog: Array<{
    action: string;
    componentId?: string;
    timestamp: string;
    details?: Record<string, unknown>;
  }>;
  rating?: { score: number; review?: string; ratedBy: string; ratedAt: string };
  createdAt: string;
  updatedAt: string;
}

// Backward compatibility aliases
/** @deprecated Use Specialization instead */
export type InstallerTrade = Specialization;
/** @deprecated Use ServiceProviderProfile instead */
export type InstallerProfile = ServiceProviderProfile;
/** @deprecated Use ClientProject instead */
export type Property = ClientProject;
/** @deprecated Use ClientProjectType instead */
export type PropertyType = ClientProjectType;
/** @deprecated Use ClientProjectStatus instead */
export type PropertyStatus = ClientProjectStatus;
